import { useState } from "react";
import {
  Stack,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Switch,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { loadConfig, saveConfig, DEFAULT_CONFIG } from "@/store/config";
import type { LlmConfig } from "@/types";

export function SettingsPanel({ onSaved }: { onSaved: () => void }) {
  const [cfg, setCfg] = useState<LlmConfig>(() => loadConfig());
  const [saved, setSaved] = useState(false);
  const [showKey, setShowKey] = useState(false);

  function set<K extends keyof LlmConfig>(key: K, value: LlmConfig[K]) {
    setCfg((c) => ({ ...c, [key]: value }));
  }

  function handleSave() {
    saveConfig(cfg);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
    onSaved();
  }

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2">
        Configure your own OpenAI-compatible LLM. Works with OpenAI, Ollama,
        LM Studio, vLLM, OpenRouter, or any server implementing{" "}
        <code>{"{baseUrl}/chat/completions"}</code>.
      </Typography>

      <TextField
        label="Base URL"
        value={cfg.baseUrl}
        onChange={(e) => set("baseUrl", e.target.value)}
        placeholder="https://api.openai.com/v1"
        size="small"
        fullWidth
      />
      <Typography variant="caption" color="text.secondary">
        Include the version path, e.g. <code>/v1</code>. For Ollama:{" "}
        <code>http://localhost:11434/v1</code>.
      </Typography>

      <TextField
        label="API Key (Bearer token)"
        type={showKey ? "text" : "password"}
        value={cfg.apiKey}
        onChange={(e) => set("apiKey", e.target.value)}
        placeholder="sk-… (leave empty for local servers that don't require auth)"
        size="small"
        fullWidth
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                onClick={() => setShowKey((s) => !s)}
                size="small"
                edge="end"
                aria-label="toggle key visibility"
              >
                {showKey ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />

      <TextField
        label="Model"
        value={cfg.model}
        onChange={(e) => set("model", e.target.value)}
        placeholder="gpt-4o-mini"
        size="small"
        fullWidth
      />
      <Typography variant="caption" color="text.secondary">
        The model id your server expects, e.g. <code>gpt-4o-mini</code>,{" "}
        <code>llama-3.1-8b</code>.
      </Typography>

      <Stack direction="row" spacing={1.5}>
        <TextField
          label="Temperature"
          type="number"
          value={cfg.temperature}
          inputProps={{ min: 0, max: 2, step: 0.1 }}
          onChange={(e) => set("temperature", Number(e.target.value))}
          size="small"
          sx={{ flex: 1 }}
        />
        <TextField
          label="Max tokens (0 = default)"
          type="number"
          value={cfg.maxTokens}
          inputProps={{ min: 0, step: 64 }}
          onChange={(e) => set("maxTokens", Number(e.target.value))}
          size="small"
          sx={{ flex: 1 }}
        />
      </Stack>

      <FormControlLabel
        control={
          <Switch
            checked={cfg.useProxy}
            onChange={(e) => set("useProxy", e.target.checked)}
            size="small"
          />
        }
        label="Route via LLM proxy (bypass CORS)"
        sx={{ ".MuiFormControlLabel-label": { fontSize: 13 } }}
      />
      <Typography variant="caption" color="text.secondary">
        On (default), requests go same-origin to <code>/api/llm</code> and the
        proxy forwards them to your base URL — so servers without CORS headers
        (OpenAI, most self-hosted) work. In dev the Vite server is the proxy; in
        production a Cloudflare Pages Function is. Turn off only if your server
        already sends CORS headers and you want a direct cross-origin call.
      </Typography>

      <Stack direction="row" spacing={1} alignItems="center">
        <Button variant="contained" onClick={handleSave}>Save</Button>
        <Button variant="text" onClick={() => setCfg({ ...DEFAULT_CONFIG })}>
          Reset to defaults
        </Button>
        {saved && <Typography color="success.main" variant="body2">Saved ✓</Typography>}
      </Stack>

      <Typography variant="caption" color="text.secondary">
        Settings are stored in this add-in's localStorage (per browser/machine).
        Your API key never leaves your machine except to reach the LLM server you
        configure. See <code>PLAN.md</code> for the upgrade path to a shared secrets backend.
      </Typography>
    </Stack>
  );
}
